import { transformPdpProduct } from "~/features/productDetails/transformers/PdpPageProductDataTransformer";
import { type PdpPageProductDataDto } from "~/features/productDetails/dto/PdpPageProductDataDto";
import { DesktopProductDetailsPage } from "~/features/productDetails/components/DesktopProductDetailsPage";

import { MobileProductDetailsPage } from "~/features/productDetails/components/MobileProductDetailsPage";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { loadPDPData } from "~/features/productDetails/redux/actions";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { useDispatch } from "react-redux";
import { useFetch } from "~/common/hooks/useFetch";
import { fetchPreferredProducts } from "~/features/productDetails/api/fetchPreferredProducts";
import { trackProduct } from "~/features/productDetails/api/trackProduct";
import { addMessage } from "~/features/toasts/redux/actions";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { RecentlyViewedProducts } from "~/features/RecentlyViewedProducts/RecentlyViewedProducts";
import { useTranslation } from "react-i18next";
import { type TechnicalInfoDataDTO } from "~/features/search/models/SearchResultsDTO";
import { useEffect, useState, type ReactNode } from "react";
import { trackPdpView } from "~/app/analytics/ga/abandonment";
import last from "lodash/last";
import { useRelatedProducts } from "~/domains/product/redux/relatedProducts/useRelatedProducts";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { useFrequentlyBoughtTogetherProducts } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/useFrequentlyBoughtTogetherProducts";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type {
  PdpPageProductData,
  PreferredProductData,
  RelatedProductData,
} from "~/features/productDetails/models/PdpPageProductData";
import type { LoadableEntry } from "~/app/store/loadableTypes";
import type { SrpProductData } from "~/utils/models";
import { FrequentlyBoughtTogetherSection } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/FrequentlyBoughtTogetherSection";
import { PurchasePlaceProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import RelatedProductsBlock from "~/features/productDetails/components/RelatedProducts/RelatedProductsBlock";
import { PDP_RESTRICTION_URL_PARAM } from "~/features/PackageRestriction/utils/constants";
import { useCalculateRestrictedCode } from "~/features/PackageRestriction/hooks/useCalculateRestrictedCode";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { PackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartForm";
import { AddToCartDerivedContextProvider } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import { FormProvider } from "react-hook-form";
import { DisplayMMDAssistantProvider } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import {
  PdpWebComponentUrlsProvider,
  type PdpWebComponentUrls,
} from "~/features/productDetails/context/PdpWebComponentUrlsContext";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { PreferredProductsSection as PreferredProductsSectionV2 } from "~/features/productDetails/components/PreferredProducts/v2/PreferredProductsSection";
import PreferredProductsSection from "~/features/productDetails/components/PreferredProducts/v1/PreferredProductsSection";
import { VariantContextProvider } from "~/features/productDetails/context/VariantContext";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

interface ProductDetailsPageContentProps {
  product: PdpPageProductData;
  showFBT: boolean;
}

export interface CommonProductDetailsPageProps {
  productCode: string;
  designation: string;
  relatedProducts: RelatedProductData[];
  relatedProductsLoading: boolean;
  preferredProducts: LoadableEntry<{
    preferred: PreferredProductData[];
    sales: SrpProductData[];
    showSimilarSalesBlock: boolean;
  }>;
}

type InnerProductDetailsPageProps = CommonProductDetailsPageProps & {
  showFBT: boolean;
  priceReferenceSelection: ReturnType<typeof usePriceReferenceSelection>["selection"];
};

const InnerProductDetailsPage = ({ showFBT, ...fullProps }: InnerProductDetailsPageProps) => {
  const preferredProductsDesign = useKameleoonFlag(KAMELEOON_FLAGS.preferredProductsDesign);
  const params = new URLSearchParams(window.location.search);
  const query = params.get(PDP_RESTRICTION_URL_PARAM) ?? "";
  const restrictedCode = useCalculateRestrictedCode(query, fullProps.productCode);

  const { form, productDescriptor, main, initialPackCodeResolved, optionalParts, prepareSubmitData } = useAddToCartForm(
    {
      productCode: fullProps.productCode,
      priceReferenceSelection: fullProps.priceReferenceSelection,
      initialPackCodeProvider: makeSalesAwarePackCodeProvider(restrictedCode),
    },
  );

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const fbtProps = useFrequentlyBoughtTogetherProducts(
    fullProps.productCode,
    showFBT && !productDescriptor.base.externalProduct,
  );
  const postSummarySlot = fbtProps.showFBTComponent && (
    <FrequentlyBoughtTogetherSection
      className={"scha__pdp__frequently-bought-together"}
      loading={fbtProps.loadingFBT}
      mainProduct={fbtProps.mainProduct}
      firstProduct={fbtProps.firstProduct}
      secondProduct={fbtProps.secondProduct}
    />
  );

  const { relatedProducts, relatedProductsLoading, preferredProducts } = fullProps;
  const product = productDescriptor.base as PdpPageProductData;

  let preferredProductsBlock;
  if (preferredProducts.state === "loading") {
    preferredProductsBlock = (
      <div className="scha__related-products">
        <CircleSpinner />
      </div>
    );
  } else if (preferredProductsDesign === "A") {
    preferredProductsBlock = (
      <PreferredProductsSectionV2
        productFamilyName={product.preferredProductsFamilyName}
        products={preferredProducts.data?.preferred ?? []}
        showAddToCart={true}
      />
    );
  } else if (preferredProductsDesign === "B") {
    preferredProductsBlock = (
      <PreferredProductsSectionV2
        productFamilyName={product.preferredProductsFamilyName}
        products={preferredProducts.data?.preferred ?? []}
        showAddToCart={false}
      />
    );
  } else {
    preferredProductsBlock = (
      <PreferredProductsSection
        productFamilyName={product.preferredProductsFamilyName}
        preferredProducts={preferredProducts}
      />
    );
  }

  preferredProductsBlock = (
    <>
      {preferredProductsBlock}
      {preferredProducts.state !== "loading" && relatedProductsLoading && (
        <div className="scha__related-products">
          <CircleSpinner />
        </div>
      )}
    </>
  );

  const finalProps: Omit<typeof fullProps, "productDescriptor"> & {
    postSummarySlot: ReactNode;
    productDescriptor: ProductDescriptor<PdpPageProductData>;
    preferredProductsBlock: ReactNode | null;
  } = {
    ...fullProps,
    postSummarySlot,
    productDescriptor: productDescriptor as ProductDescriptor<PdpPageProductData>,
    preferredProductsBlock: userIsLoggedIn() && !userIsRegistered() ? preferredProductsBlock : null,
  };

  const page = isDesktop ? <DesktopProductDetailsPage {...finalProps} /> : <MobileProductDetailsPage {...finalProps} />;

  const derivedCtx = { main, initialPackCodeResolved, optionalParts, prepareSubmitData };

  return (
    <FormProvider {...form}>
      <AddToCartDerivedContextProvider value={derivedCtx}>
        <PackageRestrictionContext value={restrictedCode}>{page}</PackageRestrictionContext>
        {(!userIsLoggedIn() || userIsRegistered()) && preferredProductsBlock}
        {relatedProducts && (
          <RelatedProductsBlock relatedProducts={relatedProducts} mainProductDesignation={product.designation} />
        )}
        <RecentlyViewedProducts variant={"pdp"} />
      </AddToCartDerivedContextProvider>
    </FormProvider>
  );
};

const ProductDetailsPageContent = (props: ProductDetailsPageContentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const product = props.product;
  const productCode = product.code;
  const [loaded, setLoaded] = useState(false);
  const { selection, status } = usePriceReferenceSelection();

  useEffect(() => {
    if (status !== "ready") {
      return;
    }
    dispatch(loadPDPData(product, getInitialPriceReference(selection)));

    trackPdpView(product, last(product.productAllCategories) ?? "");

    trackProduct(productCode);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("redirect") !== "true") {
      if (urlParams.get("isPartialMatch") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.partialMatch")));
      }
      if (urlParams.get("isNewerVersion") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.newerVersion")));
      }
    }
    setLoaded(true);
  }, [dispatch, product, productCode, selection, status, t]);

  const preferredProducts = useFetch(fetchPreferredProducts, productCode, {
    mode: "throttle",
    time: 1000,
    onLoad: (data) => {
      dispatch(addBaseProductDataAction(data?.sales ?? []));
    },
  });
  const { relatedProducts, state } = useRelatedProducts(productCode);
  const relatedProductsLoading = state === "loading";

  if (!loaded) {
    return null;
  }

  const fullProps: InnerProductDetailsPageProps = {
    productCode,
    designation: props.product.designation,
    preferredProducts,
    relatedProducts,
    relatedProductsLoading,
    showFBT: props.showFBT,
    priceReferenceSelection: selection,
  };

  return <InnerProductDetailsPage {...fullProps} />;
};

export interface ProductDetailsPageData {
  product: PdpPageProductDataDto;
  technicalInfo: TechnicalInfoDataDTO | null;
  calculationTabScriptUrl: string;
  // Toggle for frequently bought together product
  showFBT: boolean;
  displayMMDAssistant: boolean;
  mountingMgrWebComponentUrl: string;
  greaseAppWebComponentUrl: string;
  bearingXUrl: string;
}

export const ProductDetailsPage = ({ data }: { data: ProductDetailsPageData }) => {
  const product = transformPdpProduct(data.product, data.technicalInfo);

  const webComponentUrls: PdpWebComponentUrls = {
    calculationTabScriptUrl: data.calculationTabScriptUrl,
    mountingMgrWebComponentUrl: data.mountingMgrWebComponentUrl,
    greaseAppWebComponentUrl: data.greaseAppWebComponentUrl,
    bearingXUrl: data.bearingXUrl,
  };

  return (
    <PurchasePlaceProvider value={"PDP"}>
      <DisplayMMDAssistantProvider value={data.displayMMDAssistant}>
        <PdpWebComponentUrlsProvider value={webComponentUrls}>
          <VariantContextProvider variantGroupProductList={product.variantGroupProductList}>
            <ProductDetailsPageContent product={product} showFBT={data.showFBT} />
          </VariantContextProvider>
        </PdpWebComponentUrlsProvider>
      </DisplayMMDAssistantProvider>
    </PurchasePlaceProvider>
  );
};

export function createProductDetailsPage(data: ProductDetailsPageData) {
  return <ProductDetailsPage data={data} />;
}
